import { useCallback, useState, useEffect } from 'react';

const subscribers = new Map();

const notifySubscribers = (key, value) => {
  if (subscribers.has(key)) {
    subscribers.get(key).forEach((callback) => callback(value));
  }
};

export const useLocalStorage = (initialStoreKey, initialValue, options = { updateState: true, reactive: false }) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(initialStoreKey);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  // Update stored value when key changes
  useEffect(() => {
    try {
      const item = window.localStorage.getItem(initialStoreKey);
      const newValue = item ? JSON.parse(item) : initialValue;
      setStoredValue(newValue);
    } catch (error) {
      setStoredValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStoreKey]);

  useEffect(() => {
    if (!options?.reactive) {
      return undefined;
    }

    const callback = (value) => setStoredValue(value);

    if (!subscribers.has(initialStoreKey)) {
      subscribers.set(initialStoreKey, new Set());
    }
    subscribers.get(initialStoreKey).add(callback);

    return () => {
      subscribers.get(initialStoreKey).delete(callback);
      if (subscribers.get(initialStoreKey).size === 0) {
        subscribers.delete(initialStoreKey);
      }
    };
  }, [initialStoreKey, options?.reactive]);

  const storeValue = useCallback(
    (value) => {
      const valueToStore = typeof value === 'function' ? value(storedValue) : value;
      localStorage.setItem(initialStoreKey, JSON.stringify(valueToStore));

      if (options?.updateState) {
        setStoredValue(valueToStore);
      }

      notifySubscribers(initialStoreKey, valueToStore);
    },
    [initialStoreKey, options?.updateState, storedValue],
  );

  return [storedValue, storeValue];
};
